/**
 * Device Service for managing media devices
 * Handles device enumeration, selection, and permission management
 */

class DeviceService {
  constructor() {
    this.devices = {
      audioinput: [],
      videoinput: [],
      audiooutput: []
    };
    this.selectedDevices = this.loadDevicePreferences();
    this.onDevicesChangedCallback = null;
  }

  loadDevicePreferences() {
    try {
      const saved = localStorage.getItem('selectedDevices');
      return saved ? JSON.parse(saved) : {
        audioinput: null,
        videoinput: null,
        audiooutput: null
      };
    } catch (err) {
      console.error('Error loading device preferences:', err);
      return {
        audioinput: null,
        videoinput: null,
        audiooutput: null
      };
    }
  }

  saveDevicePreferences() {
    try {
      localStorage.setItem('selectedDevices', JSON.stringify(this.selectedDevices));
    } catch (err) {
      console.error('Error saving device preferences:', err);
    }
  }

  async enumerateDevices() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      
      this.devices.audioinput = devices.filter(d => d.kind === 'audioinput');
      this.devices.videoinput = devices.filter(d => d.kind === 'videoinput');
      this.devices.audiooutput = devices.filter(d => d.kind === 'audiooutput');

      // Auto-select first device if none selected
      if (!this.selectedDevices.audioinput && this.devices.audioinput.length > 0) {
        this.selectedDevices.audioinput = this.devices.audioinput[0].deviceId;
      }
      if (!this.selectedDevices.videoinput && this.devices.videoinput.length > 0) {
        this.selectedDevices.videoinput = this.devices.videoinput[0].deviceId;
      }
      if (!this.selectedDevices.audiooutput && this.devices.audiooutput.length > 0) {
        this.selectedDevices.audiooutput = this.devices.audiooutput[0].deviceId;
      }

      if (this.onDevicesChangedCallback) {
        this.onDevicesChangedCallback(this.devices);
      }

      return this.devices;
    } catch (err) {
      console.error('Error enumerating devices:', err);
      throw err;
    }
  }

  onDevicesChanged(callback) {
    this.onDevicesChangedCallback = callback;
    
    // Listen for device changes
    navigator.mediaDevices.addEventListener('devicechange', async () => {
      await this.enumerateDevices();
    });
  }

  getDevices() {
    return this.devices;
  }

  getSelectedDevices() {
    return this.selectedDevices;
  }

  selectDevice(kind, deviceId) {
    this.selectedDevices[kind] = deviceId;
    this.saveDevicePreferences();
  }

  async getUserMedia(constraints = {}) {
    const defaultConstraints = {
      audio: {
        deviceId: this.selectedDevices.audioinput 
          ? { exact: this.selectedDevices.audioinput }
          : undefined,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      },
      video: {
        deviceId: this.selectedDevices.videoinput
          ? { exact: this.selectedDevices.videoinput }
          : undefined,
        width: { ideal: 1280 },
        height: { ideal: 720 },
        frameRate: { ideal: 30 }
      }
    };

    const mergedConstraints = {
      audio: constraints.audio !== false ? {
        ...defaultConstraints.audio,
        ...(typeof constraints.audio === 'object' ? constraints.audio : {})
      } : false,
      video: constraints.video !== false ? {
        ...defaultConstraints.video,
        ...(typeof constraints.video === 'object' ? constraints.video : {})
      } : false
    };

    try {
      const stream = await navigator.mediaDevices.getUserMedia(mergedConstraints);
      return stream;
    } catch (err) {
      console.error('Error getting user media:', err);
      throw this.normalizeMediaError(err);
    }
  }

  async getDisplayMedia(constraints = {}) {
    const defaultConstraints = {
      video: {
        cursor: 'always',
        displaySurface: 'monitor'
      },
      audio: false
    };

    const mergedConstraints = {
      ...defaultConstraints,
      ...constraints
    };

    try {
      const stream = await navigator.mediaDevices.getDisplayMedia(mergedConstraints);
      return stream;
    } catch (err) {
      console.error('Error getting display media:', err);
      throw this.normalizeMediaError(err);
    }
  }

  normalizeMediaError(err) {
    const errorMap = {
      'NotAllowedError': {
        message: 'Permission denied. Please allow access to your camera and microphone.',
        userFriendly: true
      },
      'NotFoundError': {
        message: 'No camera or microphone found. Please connect a device.',
        userFriendly: true
      },
      'NotReadableError': {
        message: 'Your camera or microphone is already in use by another application.',
        userFriendly: true
      },
      'OverconstrainedError': {
        message: 'Could not start video with the requested settings.',
        userFriendly: true
      },
      'AbortError': {
        message: 'An error occurred while accessing your devices.',
        userFriendly: true
      }
    };

    const normalized = errorMap[err.name] || {
      message: err.message || 'Unknown error accessing media devices',
      userFriendly: false
    };

    return new Error(normalized.message);
  }

  async checkPermissions() {
    try {
      // Try to get a temporary stream to check permissions
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: true, 
        video: true 
      });
      stream.getTracks().forEach(track => track.stop());
      return { audio: true, video: true };
    } catch (err) {
      // Permissions denied or devices not available
      return { audio: false, video: false, error: err };
    }
  }

  async setSinkId(audioElement, deviceId) {
    if (!audioElement.setSinkId) {
      console.warn('Browser does not support audio output device selection');
      return false;
    }

    try {
      await audioElement.setSinkId(deviceId);
      return true;
    } catch (err) {
      console.error('Error setting audio output device:', err);
      return false;
    }
  }
}

export default DeviceService;
